import { useEffect, useRef } from 'react'
import { Workflow, CircleDot, Sword } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import type { ChatCommandDescriptor, WorkflowDefinitionSummary, Skill } from '@/types'
import type { SlashItem } from './slashCommandItems'

interface SlashCommandPickerProps {
  items: SlashItem[]
  filter: string
  selectedIndex: number
  onSelectCommand: (command: ChatCommandDescriptor) => void
  onSelectWorkflow: (workflow: WorkflowDefinitionSummary) => void
  onSelectSkill: (skill: Skill) => void
  skillsLoading?: boolean
}

function groupLabelFor(item: SlashItem): string {
  if (item.type === 'workflow') return 'Workflows'
  if (item.type === 'skill') return 'Skills'
  return item.command.category
}

export function SlashCommandPicker({
  items,
  filter,
  selectedIndex,
  onSelectCommand,
  onSelectWorkflow,
  onSelectSkill,
  skillsLoading = false,
}: Readonly<SlashCommandPickerProps>) {
  const listRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const el = listRef.current?.querySelector(`[data-index="${selectedIndex}"]`)
    el?.scrollIntoView({ block: 'nearest' })
  }, [selectedIndex])

  if (items.length === 0) {
    return (
      <div className="absolute bottom-full left-0 right-0 mb-1 bg-popover border rounded-lg shadow-lg p-3 z-50">
        <p className="text-xs text-muted-foreground" role="status">
          {skillsLoading ? 'Loading skills…' : `No commands match "/${filter}"`}
        </p>
      </div>
    )
  }

  const groupLabels = items.map(groupLabelFor)

  return (
    <div
      ref={listRef}
      className="absolute bottom-full left-0 right-0 mb-1 bg-popover border rounded-lg shadow-lg z-50 max-h-72 overflow-y-auto"
      data-testid="slash-command-picker"
    >
      {items.map((item, idx) => {
        const groupLabel = groupLabels[idx]
        const isNewGroup = idx === 0 || groupLabels[idx - 1] !== groupLabel

        const isSelected = idx === selectedIndex
        const rowClassName = `w-full text-left flex items-start gap-3 px-3 py-2 transition-colors ${
          isSelected ? 'bg-accent text-accent-foreground' : 'hover:bg-muted'
        }`

        return (
          <div key={`${item.type}-${item.type === 'workflow' ? item.workflow.id : item.type === 'command' ? item.command.name : item.skill.id}`}>
            {isNewGroup && (
              <div className="px-3 pt-2 pb-1">
                <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                  {groupLabel}
                </p>
              </div>
            )}
            {item.type === 'workflow' && (
              <button
                data-index={idx}
                data-testid={`slash-command-item-${item.workflow.name}`}
                className={rowClassName}
                onMouseDown={(e) => {
                  e.preventDefault()
                  onSelectWorkflow(item.workflow)
                }}
              >
                <Workflow className="h-4 w-4 mt-0.5 shrink-0 text-muted-foreground" />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium">{item.workflow.displayName}</span>
                    <Badge variant="secondary" className="text-[10px]">v{item.workflow.version}</Badge>
                  </div>
                  <p className="text-xs text-muted-foreground truncate">{item.workflow.description}</p>
                </div>
              </button>
            )}
            {item.type === 'command' && (
              <button
                data-index={idx}
                data-testid={`slash-command-item-${item.command.name}`}
                className={rowClassName}
                onMouseDown={(e) => {
                  e.preventDefault()
                  onSelectCommand(item.command)
                }}
              >
                <CircleDot className="h-4 w-4 mt-0.5 shrink-0 text-muted-foreground" />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium">/{item.command.name}</span>
                  </div>
                  <p className="text-xs text-muted-foreground truncate">{item.command.description}</p>
                </div>
              </button>
            )}
            {item.type === 'skill' && (
              <button
                data-index={idx}
                data-testid={`slash-command-item-${item.skill.name}`}
                className={rowClassName}
                onMouseDown={(e) => {
                  e.preventDefault()
                  onSelectSkill(item.skill)
                }}
              >
                <Sword className="h-4 w-4 mt-0.5 shrink-0 text-muted-foreground" />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium">{item.skill.displayName}</span>
                    {item.skill.latestVersion && <Badge variant="secondary" className="text-[10px]">v{item.skill.latestVersion}</Badge>}
                  </div>
                  {item.skill.description && <p className="text-xs text-muted-foreground truncate">{item.skill.description}</p>}
                </div>
              </button>
            )}
          </div>
        )
      })}
    </div>
  )
}
